import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

interface HRAuthGuardProps {
  children: React.ReactNode
  requiredRole?: 'HR_STAFF' | 'HR_ADMIN' | 'GROUP_ADMIN'
}

export function HRAuthGuard({ children, requiredRole }: HRAuthGuardProps) {
  const { isAuthenticated, userType, hrUser, isLoading, refreshSession } = useAuth()
  const location = useLocation()

  // Refresh session on mount to ensure we have fresh auth state
  useEffect(() => {
    if (!isLoading) {
      refreshSession()
    }
  }, [refreshSession, isLoading])

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Redirect to HR login if not authenticated or not HR user
  if (!isAuthenticated || userType !== 'hr' || !hrUser) {
    return (
      <Navigate 
        to="/admin/login" 
        state={{ from: location }} 
        replace 
      />
    )
  }

  // Check role-based access if required
  if (requiredRole && hrUser) {
    const roleHierarchy = {
      'HR_STAFF': 1,
      'HR_ADMIN': 2,
      'GROUP_ADMIN': 3,
    }

    const userLevel = roleHierarchy[hrUser.role] || 0
    const requiredLevel = roleHierarchy[requiredRole] || 0

    if (userLevel < requiredLevel) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have sufficient permissions to access this page.
            </p>
            <p className="text-sm text-muted-foreground">
              Required: {requiredRole} | Your role: {hrUser.role}
            </p>
          </div>
        </div>
      )
    }
  }

  // Render children if all checks pass
  return <>{children}</>
}

// Convenience component for different role requirements
export const HRAdminGuard = ({ children }: { children: React.ReactNode }) => (
  <HRAuthGuard requiredRole="HR_ADMIN">{children}</HRAuthGuard>
)

export const GroupAdminGuard = ({ children }: { children: React.ReactNode }) => (
  <HRAuthGuard requiredRole="GROUP_ADMIN">{children}</HRAuthGuard>
)